import * as React from "react";
import {
  Typography,
  Box,
  Chip,
  IconButton,
  Card,
  CardActionArea,
} from "@mui/material";
import { connect } from "react-redux";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import StarIcon from "@mui/icons-material/Star";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import { Link } from "react-router-dom";
import moment from "moment";
import {
  addFeaturedEvent,
  removeFeaturedEvent,
} from "../store/actions/adminActions";
import {
  marketingColors,
  marketingFonts,
  marketingShadow,
  marketingRadius,
} from "./tickitMarketingTheme";

function eventStatus(event) {
  if (!event?.date) return { label: "Draft", variant: "gray" };
  if (moment().isAfter(moment(event.date))) return { label: "Past", variant: "gray" };
  const days = moment(event.date).diff(moment(), "days");
  if (days <= 0) return { label: "Today", variant: "amber" };
  return { label: "Upcoming", variant: "purple" };
}

const badgeStyles = {
  purple: { bg: marketingColors.brandPale, color: marketingColors.brand },
  amber: { bg: "#FEF3C7", color: "#92400E" },
  gray: { bg: "#F3F4F6", color: "#4B5563" },
};

function EventCard({ event, token, addFeaturedEvent, removeFeaturedEvent }) {
  const role = token?.split(" ")[0];
  const eventPath =
    role === "Admin" || role === "Super" || role === "Partner"
      ? `/event/dashboard/${event._id}`
      : `/event/${event._id}`;
  const status = eventStatus(event);
  const badge = badgeStyles[status.variant];
  const bannerGradient = event?.corporate
    ? "linear-gradient(135deg, #1a0a30 0%, #46109B 100%)"
    : "linear-gradient(135deg, #0f2027 0%, #203a43 50%, #2c5364 100%)";

  const handleFeature = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!event.featured) {
      addFeaturedEvent({ eventId: event._id });
    } else {
      removeFeaturedEvent({ eventId: event._id });
    }
  };

  return (
    <Card
      elevation={0}
      sx={{
        backgroundColor: marketingColors.surface,
        borderRadius: marketingRadius.lg,
        border: `1px solid ${marketingColors.border}`,
        boxShadow: marketingShadow.card,
        p: 0,
        overflow: "hidden",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        transition: "all 0.2s ease",
        cursor: "pointer",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: marketingShadow.cardHover,
          borderColor: marketingColors.brand,
        },
        "&:focus-within": {
          outline: `2px solid ${marketingColors.brand}`,
          outlineOffset: 2,
        },
      }}
    >
      <CardActionArea
        component={Link}
        to={eventPath}
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          height: "100%",
        }}
      >
        <Box
          sx={{
            position: "relative",
            height: 80,
            background: event?.image
              ? `url(${event.image}) center/cover no-repeat`
              : bannerGradient,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {!event?.image && (
            <Typography sx={{ fontSize: 26 }}>{event?.corporate ? "🎉" : "🎤"}</Typography>
          )}
          {event.featured && (
            <Chip
              label="Featured"
              size="small"
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                bgcolor: marketingColors.brand,
                color: "#fff",
                fontFamily: marketingFonts.body,
                fontWeight: 700,
                fontSize: "0.6875rem",
                height: 22,
              }}
            />
          )}
          {role === "Super" && (
            <IconButton
              onClick={handleFeature}
              aria-label={event.featured ? "Remove from featured" : "Add to featured"}
              sx={{
                position: "absolute",
                top: 6,
                left: 6,
                bgcolor: "rgba(14, 5, 32, 0.55)",
                color: event.featured ? marketingColors.p300 : "#fff",
                "&:hover": { bgcolor: "rgba(14, 5, 32, 0.75)" },
              }}
            >
              {event.featured ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
            </IconButton>
          )}
        </Box>

        <Box sx={{ p: "13px", flexGrow: 1, display: "flex", flexDirection: "column", width: "100%" }}>
          <Typography
            sx={{
              fontFamily: marketingFonts.display,
              fontWeight: 700,
              fontSize: "13.5px",
              color: marketingColors.text,
              mb: 0.5,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {event.title}
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.625, mb: 0.5 }}>
            <CalendarMonthOutlinedIcon sx={{ fontSize: 14, color: marketingColors.text3 }} />
            <Typography sx={{ fontSize: "11.5px", color: marketingColors.text3, fontFamily: marketingFonts.body }}>
              {event?.date ? moment(event.date).format("D MMM YYYY · h:mm A") : "Date TBD"}
            </Typography>
          </Box>

          {event?.location && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.625, mb: 1 }}>
              <LocationOnOutlinedIcon sx={{ fontSize: 14, color: marketingColors.text3 }} />
              <Typography
                sx={{
                  fontSize: "11.5px",
                  color: marketingColors.text3,
                  fontFamily: marketingFonts.body,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {event.location}
              </Typography>
            </Box>
          )}

          <Box sx={{ mt: "auto", display: "flex", alignItems: "center", justifyContent: "space-between", pt: 1.25 }}>
            <Chip
              label={status.label}
              size="small"
              sx={{
                bgcolor: badge.bg,
                color: badge.color,
                fontFamily: marketingFonts.body,
                fontWeight: 700,
                fontSize: "11px",
                height: 22,
                borderRadius: "20px",
              }}
            />
            <Typography sx={{ fontFamily: marketingFonts.body, fontSize: "12px", fontWeight: 600, color: marketingColors.brand }}>
              View →
            </Typography>
          </Box>
        </Box>
      </CardActionArea>
    </Card>
  );
}

const mapStateToProps = (state) => ({
  token: state?.auth?.token,
});

const mapDispatchToProps = {
  addFeaturedEvent,
  removeFeaturedEvent,
};

export default connect(mapStateToProps, mapDispatchToProps)(EventCard);
